<template>
  <div class="space-y-4">
    <div class="kpi-table-wrapper w-full border border-[#E0E0E04A] p-6 rounded-xl bg-[#F5F6FAB3]">
      <div>
        <div class="w-full">
          <div class="kpi-grid">
            <div
              v-for="header in headers"
              :key="header"
              class="h-[56px] px-4 flex items-center justify-center text-center text-[#00423D] font-semibold text-[12px] leading-[105%] uppercase font-['Open_Sans'] bg-white border-r border-[#E0E0E04A] first:rounded-tl-[12px] last:rounded-tr-[12px] last:border-r-0"
            >
              {{ header }}
            </div>
          </div>

          <div v-if="isLoading" class="bg-white border-t border-[#E0E0E04A] rounded-b-[12px] p-8 text-center text-[13px] text-[#414141] font-['Open_Sans']">
            Loading KPIs...
          </div>

          <div v-else>
            <div
              v-for="(row, index) in rows"
              :key="row.localId"
              class="kpi-grid kpi-row bg-white border-t border-[#E0E0E04A]"
            >
              <div class="cell">
                <textarea
                  v-model="row.title"
                  rows="2"
                  class="field-text cell-textarea"
                  placeholder="Write here"
                  @input="handleInput(index)"
                />
              </div>

              <div class="cell">
                <textarea
                  v-model="row.description"
                  rows="4"
                  class="field-text cell-textarea"
                  placeholder="Write here"
                  @input="handleInput(index)"
                />
              </div>

              <div class="cell">
                <textarea
                  v-model="row.baselineMetric"
                  rows="2"
                  class="field-text cell-textarea"
                  placeholder="Write here"
                  @input="handleInput(index)"
                />
              </div>

              <div class="cell">
                <textarea
                  v-model="row.targetMetric"
                  rows="2"
                  class="field-text cell-textarea"
                  placeholder="Write here"
                  @input="handleInput(index)"
                />
              </div>

              <div class="cell">
                <div class="date-field">
                  <input
                    v-model="row.targetDate"
                    type="date"
                    class="field-text date-input"
                    @input="handleInput(index)"
                  />
                  <img class="date-icon" src="/images/calendar.svg" alt="Calendar" />
                </div>
              </div>

              <div class="cell justify-center items-start">
                <button
                  type="button"
                  class="text-[#851208] mt-[2px]"
                  @click="deleteRow(index)"
                >
                  <svg width="17" height="17" viewBox="0 0 11 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.5249 12.4498C2.17428 12.4498 1.87412 12.325 1.62443 12.0753C1.37475 11.8256 1.2499 11.5254 1.2499 11.1748V2.6748H0.399902V1.3998H3.7999V0.549805H7.1999V1.3998H10.5999V2.6748H9.7499V11.1672C9.7499 11.5264 9.62506 11.83 9.37537 12.0779C9.12568 12.3258 8.82553 12.4498 8.4749 12.4498H2.5249ZM8.4749 2.6748H2.5249V11.1748H8.4749V2.6748ZM3.7999 9.89981H5.0749V3.9498H3.7999V9.89981ZM5.9249 9.89981H7.1999V3.9498H5.9249V9.89981Z" fill="currentColor"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="pl-4 pt-4">
        <button
          type="button"
          class="text-[#414141] text-[10px] leading-[105%] uppercase font-['Open_Sans'] font-semibold underline"
          @click="addRow"
        >
          + ADD MORE
        </button>
      </div>
    </div>

    <div v-if="validationError" class="text-red-500 text-sm">
      {{ validationError }}
    </div>
  </div>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import global from '@/global';

const props = defineProps({
  programId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['data-changed']);

const authStore = useAuthStore();

const headers = [
  'KPI Title',
  'KPI Description',
  'Baseline Metric',
  'KPI Target Metric',
  'Target Date',
  '',
];

const rows = ref([]);
const validationError = ref('');
const isLoading = ref(false);
const isSaving = ref(false);
const autoSaveTimer = ref(null);
const pendingSaveRowId = ref(null);
const rowSnapshots = ref({});

const createRow = (overrides = {}) => ({
  localId: overrides.localId || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  id: overrides.id ?? null,
  title: overrides.title ?? '',
  description: overrides.description ?? '',
  baselineMetric: overrides.baselineMetric ?? '',
  targetMetric: overrides.targetMetric ?? '',
  targetDate: overrides.targetDate ?? '',
  actualResultsAchieved: overrides.actualResultsAchieved ?? '',
  assignedUserIds: Array.isArray(overrides.assignedUserIds) ? [...overrides.assignedUserIds] : [],
  assignedUsers: Array.isArray(overrides.assignedUsers) ? [...overrides.assignedUsers] : [],
});

const authHeaders = computed(() => ({
  Authorization: `Bearer ${authStore.user.accessToken}`,
  'Content-Type': 'application/json',
}));

const isRowEmpty = (row) => {
  return !row.title?.trim()
    && !row.description?.trim()
    && !row.baselineMetric?.trim()
    && !row.targetMetric?.trim()
    && !row.targetDate
    && !row.actualResultsAchieved?.trim()
    && row.assignedUserIds.length === 0;
};

const ensureAtLeastOneRow = () => {
  if (rows.value.length === 0) {
    rows.value = [createRow()];
  }
};

const getRowPayload = (row) => ({
  id: row.id || undefined,
  title: row.title.trim(),
  description: row.description.trim(),
  baseline_metric: row.baselineMetric.trim(),
  kpi_target_metric: row.targetMetric.trim(),
  target_date: row.targetDate || null,
  actual_results_achieved: row.actualResultsAchieved?.trim() || '',
  assigned_user_ids: [...row.assignedUserIds],
});

const getRowSnapshotKey = (row) => JSON.stringify(getRowPayload(row));

const setRowSnapshot = (row) => {
  rowSnapshots.value = {
    ...rowSnapshots.value,
    [row.localId]: getRowSnapshotKey(row),
  };
};

const removeRowSnapshot = (localId) => {
  const nextSnapshots = { ...rowSnapshots.value };
  delete nextSnapshots[localId];
  rowSnapshots.value = nextSnapshots;
};

const refreshRowSnapshots = (nextRows) => {
  const nextSnapshots = {};
  nextRows.forEach((row) => {
    nextSnapshots[row.localId] = getRowSnapshotKey(row);
  });
  rowSnapshots.value = nextSnapshots;
};

const hasRowChanged = (row) => {
  return rowSnapshots.value[row.localId] !== getRowSnapshotKey(row);
};

const mapTeamMember = (member = {}) => ({
  id: member.id,
  name: member.name || (member.first_name && member.last_name ? `${member.first_name} ${member.last_name}` : member.email),
  email: member.email,
  logo_url: member.logo_url,
  first_name: member.first_name,
  last_name: member.last_name,
});

const mapApiKpi = (kpi = {}, localId = null) => {
  const assignedUsers = Array.isArray(kpi.assigned_users)
    ? kpi.assigned_users.map(mapTeamMember)
    : Array.isArray(kpi.assignedUsers)
      ? kpi.assignedUsers.map(mapTeamMember)
      : [];

  return createRow({
    localId: localId || `kpi-${kpi.id || Math.random().toString(36).slice(2, 9)}`,
    id: kpi.id ?? null,
    title: kpi.title ?? '',
    description: kpi.description ?? '',
    baselineMetric: kpi.baseline_metric ?? '',
    targetMetric: kpi.kpi_target_metric ?? '',
    targetDate: kpi.target_date ?? '',
    actualResultsAchieved: kpi.actual_results_achieved ?? '',
    assignedUserIds: assignedUsers.map((user) => user.id),
    assignedUsers,
  });
};

const fetchKpis = async () => {
  if (!props.programId || !authStore.user?.accessToken) {
    rows.value = [createRow()];
    return;
  }

  try {
    isLoading.value = true;
    const res = await fetch(`${global.API_URL}/kpis/core_program/${props.programId}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    const data = await res.json();
    const apiKpis = Array.isArray(data.kpis)
      ? data.kpis
      : Array.isArray(data.data)
        ? data.data
        : [];

    rows.value = apiKpis.length > 0
      ? apiKpis.map((kpi) => mapApiKpi(kpi))
      : [createRow()];
    refreshRowSnapshots(rows.value);
  } catch (error) {
    console.error('Failed to fetch KPIs:', error);
    rows.value = [createRow()];
    refreshRowSnapshots(rows.value);
  } finally {
    isLoading.value = false;
  }
};

const upsertSingleRow = async (row) => {
  const payload = {
    kpi: getRowPayload(row),
  };

  const res = await fetch(`${global.API_URL}/kpis/core_program/${props.programId}`, {
    method: 'POST',
    headers: authHeaders.value,
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    throw new Error(`HTTP error! Status: ${res.status}`);
  }

  const data = await res.json();
  return mapApiKpi(data.kpi || {}, row.localId);
};

const saveRowByLocalId = async (localId, { silent = false } = {}) => {
  if (!props.programId || !authStore.user?.accessToken) {
    if (!silent) {
      validationError.value = 'Program not found. Please try again.';
    }
    return false;
  }

  const rowIndex = rows.value.findIndex((row) => row.localId === localId);
  if (rowIndex === -1) {
    return true;
  }

  const row = rows.value[rowIndex];

  if (isRowEmpty(row)) {
    if (!row.id) {
      setRowSnapshot(row);
    }
    return true;
  }

  if (!hasRowChanged(row)) {
    return true;
  }

  if (isSaving.value) {
    return false;
  }

  try {
    isSaving.value = true;
    if (!silent) {
      validationError.value = '';
    }

    const savedRow = await upsertSingleRow(row);
    rows.value[rowIndex] = savedRow;
    setRowSnapshot(savedRow);
    return true;
  } catch (error) {
    console.error('Failed to save KPI row:', error);
    if (!silent) {
      validationError.value = 'Failed to save KPIs. Please try again.';
    }
    return false;
  } finally {
    isSaving.value = false;
  }
};

const saveRows = async ({ silent = false } = {}) => {
  const rowsToSave = rows.value.filter((row) => !isRowEmpty(row) && hasRowChanged(row));

  if (rowsToSave.length === 0) {
    return true;
  }

  for (const row of rowsToSave) {
    const success = await saveRowByLocalId(row.localId, { silent });
    if (!success) {
      return false;
    }
  }

  return true;
};

const scheduleAutoSave = (localId) => {
  if (autoSaveTimer.value) {
    clearTimeout(autoSaveTimer.value);
  }

  pendingSaveRowId.value = localId;
  autoSaveTimer.value = setTimeout(async () => {
    const rowId = pendingSaveRowId.value;
    pendingSaveRowId.value = null;
    if (rowId) {
      await saveRowByLocalId(rowId, { silent: true });
    }
  }, 800);
};

const triggerAutoSave = (index) => {
  emit('data-changed');
  validationError.value = '';
  const row = rows.value[index];
  if (row) {
    scheduleAutoSave(row.localId);
  }
};

const handleInput = (index) => {
  triggerAutoSave(index);
};

const addRow = () => {
  rows.value.push(createRow());
  validationError.value = '';
};

const deleteRow = async (index) => {
  const row = rows.value[index];

  if (row?.id) {
    try {
      const res = await fetch(`${global.API_URL}/kpis/${row.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
    } catch (error) {
      console.error('Failed to delete KPI:', error);
      validationError.value = 'Failed to delete KPI. Please try again.';
      return;
    }
  }

  removeRowSnapshot(row.localId);
  rows.value.splice(index, 1);
  ensureAtLeastOneRow();
  emit('data-changed');
};

const validate = async () => {
  validationError.value = '';
  return saveRows();
};

watch(
  () => props.programId,
  async () => {
    if (autoSaveTimer.value) {
      clearTimeout(autoSaveTimer.value);
      pendingSaveRowId.value = null;
    }
    await fetchKpis();
  },
  { immediate: true }
);

onUnmounted(() => {
  if (autoSaveTimer.value) {
    clearTimeout(autoSaveTimer.value);
  }
});

defineExpose({
  saveRows,
  validate,
});
</script>

<style scoped>
.kpi-grid {
  display: grid;
  grid-template-columns: 16% 26% 17% 18% 18% 5%;
  width: 100%;
}

.kpi-row {
  border-top: 1px solid rgba(224, 224, 224, 0.29);
}

.field-text {
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 105%;
  letter-spacing: 0;
  color: #414141;
}

.cell {
  min-height: 84px;
  padding: 12px;
  display: flex;
  align-items: flex-start;
  border-right: 1px solid rgba(224, 224, 224, 0.29);
  min-width: 0;
}

.cell:last-child {
  border-right: 0;
}

.cell-textarea {
  width: 100%;
  min-height: 56px;
  resize: none;
  border: none;
  background: transparent;
  outline: none;
}

.cell-textarea::placeholder {
  color: #c5c5c5;
  font-style: italic;
}

.date-field {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
}

.date-input {
  width: 100%;
  border: none;
  background: transparent;
  outline: none;
  padding-right: 24px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
}

.date-input::-webkit-calendar-picker-indicator {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.date-input::-webkit-datetime-edit,
.date-input::-webkit-datetime-edit-text,
.date-input::-webkit-datetime-edit-month-field,
.date-input::-webkit-datetime-edit-day-field,
.date-input::-webkit-datetime-edit-year-field {
  color: #414141;
}

.date-icon {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  pointer-events: none;
}

@media (max-width: 1200px) {
  .kpi-grid {
    grid-template-columns: 15% 25% 17% 18% 20% 5%;
  }
}
</style>
